import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./NewEventModal.module.css";
import { Modal } from "shared/Modal";
import classNames from "classnames";
import { Select } from "shared/Select/Select";
import { useHistory } from "react-router-dom";
import NewEventNameModal from "Events/Components/NewEventNameModal";
import { useCreateEvent } from "./useCreateEvent";
import { useCurrentRoute } from "../../../../src/shared/@hooks/useCurrentRoute";
import { useEventsVirtualGet } from "./useEventsVirtualGet";
import DatePicker from "../DatePicker";
import moment from "moment-timezone";
import Checkbox from "shared/@forms/Checkbox";
import Loader from "shared/Loader";
import {
  DEFAULT_TIMEZONE_LABEL,
  buildUtcFromLocalParts,
} from "shared/dateTimeHelpers";

export function NewEventModal({
  isOpen,
  onCollapse,
  onChange,
  selectedRuleset,
  selectedEventName,
  isFromRuleset,
  isFromEventDetails,
}) {
  const history = useHistory();

  const { createEvent, createNewEvent, eventUpdatePut } =
    useCreateEvent(isOpen);

  const {
    rulesetData,
    rulesetStatus,
    refetchRulesetData,
    isRulesetDataLoading,
  } = useCurrentRoute();

  const [virtualEvents, setVirtualEvents] = useState(null);
  const [selectedVirtualEventId, setSelectedVirtualEventId] = useState(null);

  const [ruleset, setRuleset] = useState(selectedRuleset || null);
  const [eventName, setEventName] = useState(selectedEventName || null);

  const {
    data: virtualEventsData,
    isLoading: isVirtualEventsLoading,
    refetch: refetchVirtualEvents,
  } = useEventsVirtualGet({ ruleset: ruleset, eventName: eventName });

  useEffect(() => {
    if (!isVirtualEventsLoading && virtualEventsData) {
      setVirtualEvents(virtualEventsData);
    }
  }, [isVirtualEventsLoading, virtualEventsData]);

  useEffect(() => {
    if (ruleset && eventName) {
      refetchVirtualEvents();
    }
  }, [ruleset, eventName]);

  const initialEventType = {
    ruleset: "",
    eventName: "",
    location: "Onsite",
  };

  const [eventType, setEventType] = useState(initialEventType);
  const [openNewEventModal, setOpenNewEventModal] = useState(false);
  const [eventNameOptions, setEventNameOptions] = useState([]);
  const [eventsOptions, setEventsOptions] = useState([]);
  const [isChooseDateModalOpen, setIsChooseDateModalOpen] = useState(false);
  const [createdEventData, setCreatedEventData] = useState(null);
  const todaysDate = new Date();
  const [date, setDate] = useState(todaysDate);
  const [date2, setDate2] = useState("");

  const getPickerDateString = (value) => {
    if (!value) return "";
    return moment(value).format("MM/DD/YYYY");
  };

  const getSelectedLocalDate = (value) => {
    return getPickerDateString(value);
  };

  const buildPacificUtc = (value, localTime) => {
    const localDate = getSelectedLocalDate(value);

    return buildUtcFromLocalParts({
      localDate,
      localTime,
      timeZone: DEFAULT_TIMEZONE_LABEL,
    });
  };

  const buildPacificUtcFromDateString = (localDate, localTime) => {
    return buildUtcFromLocalParts({
      localDate,
      localTime,
      timeZone: DEFAULT_TIMEZONE_LABEL,
    });
  };

  useEffect(() => {
    if (eventType.ruleset && eventType.eventName) {
      setRuleset(eventType.ruleset);
      setEventName(eventType.eventName);
    }
  }, [eventType]);

  //Handles ruleset selection
  useEffect(() => {
    if (!rulesetData || !Array.isArray(rulesetData)) {
      setEventsOptions([]);
      return;
    }

    const newEventsOptions = rulesetData.reduce((acc, ruleset) => {
      if (ruleset.events && Array.isArray(ruleset.events)) {
        const filteredEvents = ruleset.events
          .filter((event) => event.name)
          .map((event) => ({
            eventName: event.name.trim(),
            rulesetName: ruleset.name.trim(),
          }));
        acc.push(...filteredEvents);
      }
      return acc;
    }, []);

    setEventsOptions(newEventsOptions);
  }, [rulesetData]);

  const rulesetOptions = useMemo(() => {
    return [...new Set(eventsOptions.map((event) => event.rulesetName))];
  }, [eventsOptions]);

  const handleRulesetChange = (selectedOption) => {
    if (!rulesetData) {
      return;
    }

    setEventType((prev) => ({
      ...prev,
      ruleset: selectedOption,
      eventName: "",
    }));

    //Filter event names for the selected ruleset
    const filteredEventNames = eventsOptions
      .filter((eventOption) => eventOption.rulesetName === selectedOption)
      .map((eventOption) => eventOption.eventName);

    const selectedRuleset = rulesetData.find(
      (ruleset) => ruleset.name === selectedOption
    );

    if (
      selectedRuleset &&
      selectedRuleset.name &&
      (selectedRuleset.name.includes("2") || selectedRuleset.name.includes("3"))
    ) {
      filteredEventNames.push("+ Create New");
    }

    setEventNameOptions(filteredEventNames);
  };

  const handleLocation = () => {
    setEventType((prevData) => ({
      ...prevData,
      location: prevData.location === "Onsite" ? "Virtual" : "Onsite",
    }));
  };

  //Handles event name change
  const handleEventNameChange = (selectedRuleset, newEventName) => {
    if (newEventName === "+ Create New") {
      setOpenNewEventModal(true);
    } else {
      setEventType((prevData) => ({ ...prevData, eventName: newEventName }));
    }
  };

  useEffect(() => {
    if (selectedRuleset && rulesetData) {
      setEventNameOptions([
        ...rulesetData
          .find((set) => set.name == selectedRuleset)
          ?.events?.map((event) => event.name),
        "+ Create New",
      ]);
    }
  }, [selectedRuleset, rulesetData]);

  //Handles the preselection of ruleset and event name in ruleset page
  useEffect(() => {
    if (isOpen && selectedEventName) {
      const foundEventOption = eventsOptions.find(
        (option) => option.eventName === selectedEventName
      );

      if (foundEventOption) {
        setEventType({
          ...initialEventType,
          ruleset: selectedRuleset || foundEventOption.rulesetName,
          eventName: foundEventOption.eventName,
        });
      }
    }
  }, [isOpen, selectedRuleset, selectedEventName, eventsOptions]);

  //Handles creating a new event name
  const onSaveNewEvent = async (newEventName, image) => {
    if (!rulesetData) {
      return "Ruleset data is missing.";
    }

    let error = null;
    let rulesetIds = {};

    rulesetData.forEach((ruleset) => {
      rulesetIds[ruleset.name] = ruleset.id;
    });

    const rulesetId = rulesetIds[eventType.ruleset];

    if (rulesetId) {
      const newEventData = {
        rulesetId: rulesetId,
        eventName: newEventName,
        eventIcon: image,
      };

      try {
        await createNewEvent.mutateAsync(newEventData);
      } catch (err) {
        error = err.message;
      }
    } else {
      error = "Invalid ruleset ID.";
    }

    if (error) {
      return error;
    }

    // Update UI only if there's no error
    setEventNameOptions((prevState) => {
      const filteredOptions = prevState.filter(
        (option) => option !== "+ Create New"
      );

      if (!filteredOptions.includes(newEventName)) {
        filteredOptions.push(newEventName);
      }

      return [...filteredOptions, "+ Create New"];
    });

    setEventType((prev) => ({ ...prev, eventName: newEventName }));
    setOpenNewEventModal(false);

    return null;
  };

  //Handles create button and redirect page
  const handleCreate = async () => {
    if (!rulesetData) {
      return;
    }

    const newEvent = {
      name: eventType.eventName,
      location: eventType.location.toLowerCase(),
      ruleset: eventType.ruleset,
    };

    const createdEvent = await createEvent(newEvent);

    if (selectedVirtualEventId) {
      const virtualEvent = virtualEvents.find(
        (event) => event.id === selectedVirtualEventId
      );

      const object = {
        virtual: {
          linkedEvent: {
            eventId: virtualEvent?.sync?.eventId,
            id: selectedVirtualEventId || "",
            name:
              virtualEvent?.marketing?.title?.saved ||
              virtualEvent?.marketing?.title?.value ||
              "",
          },
          eventLink: virtualEvent?.link || "",
          eventType: virtualEvent?.type || "",
        },
      };

      const id = createdEvent.id;

      await eventUpdatePut.mutateAsync({
        eventObject: object,
        eventId: id,
      });
    }

    if (newEvent.ruleset === "Major v1.0") {
      history.push(`/events/edit/event/majorv1/${createdEvent.id}`);
    } else if (newEvent.ruleset === "Local v1.0") {
      setIsChooseDateModalOpen(true);
      setCreatedEventData(createdEvent);
    } else if (newEvent.ruleset === "LOS v1.0") {
      history.push(`/events/edit/event/losv1/${createdEvent.id}`);
    } else if (newEvent.ruleset === "Major v2.0") {
      history.push(`/events/edit/event/majorv2/${createdEvent.id}`);
    } else if (newEvent.ruleset === "Local v2.0") {
      setCreatedEventData(createdEvent);

      if (date2) {
        const newSessions = [];

        if (eventType.eventName === "Regional") {
          newSessions.push({
            type: "",
            name: "Seminar",
            description: "",
            speakers: [],
            location: "",
            capacity: "",
            startDate: buildPacificUtc(date2, "16:00"),
            endDate: buildPacificUtc(date2, "18:30"),
            // checkInStartDate: eventStartDate
            //   .clone()
            //   .add(14, "hours")
            //   .toISOString(),
            // checkInEndDate: eventStartDate
            //   .clone()
            //   .add(17.5, "hours")
            //   .toISOString(),
            // doorsOpenStartDate: eventStartDate
            //   .clone()
            //   .add(14, "hours")
            //   .toISOString(),
            visibleTo: [],
            visibility: "All",
          });

          newSessions.push({
            type: "",
            name: "Second Look Meeting\u2122",
            description: "",
            speakers: [],
            location: "",
            capacity: "",
            startDate: buildPacificUtc(date2, "20:00"),
            endDate: buildPacificUtc(date2, "22:30"),
            // checkInStartDate: eventStartDate
            //   .clone()
            //   .add(19, "hours")
            //   .toISOString(),
            // checkInEndDate: eventStartDate
            //   .clone()
            //   .add(22.5, "hours")
            //   .toISOString(),
            // doorsOpenStartDate: eventStartDate
            //   .clone()
            //   .add(19, "hours")
            //  .toISOString(),
            visibleTo: [],
            visibility: "All",
          });
        } else {
          newSessions.push({
            type: "",
            name: "Second Look Meeting\u2122",
            description: "",
            speakers: [],
            location: "",
            capacity: "",
            startDate: buildPacificUtc(date2, "20:00"),
            endDate: buildPacificUtc(date2, "22:00"),
            // checkInStartDate: eventStartDate
            //   .clone()
            //   .add(19, "hours")
            //   .toISOString(),
            // checkInEndDate: eventStartDate
            //   .clone()
            //   .add(22, "hours")
            //   .toISOString(),
            // doorsOpenStartDate: eventStartDate
            //   .clone()
            //   .add(19, "hours")
            //   .toISOString(),
            visibleTo: [],
            visibility: "All",
          });
        }

        if (createdEvent.event?.location?.value === "onsite") {
          await eventUpdatePut.mutateAsync({
            eventObject: { sessions: newSessions },
            eventId: createdEvent.id,
          });
        }

        await handleDateSaveClick(true, createdEvent);
      }

      history.push(`/events/edit/event/localv2/${createdEvent.id}`);
    } else if (newEvent.ruleset === "LOS v2.0") {
      history.push(`/events/edit/event/losv2/${createdEvent.id}`);
    } else if (newEvent.ruleset === "LOS v3.0") {
      history.push(`/events/edit/event/losv3/${createdEvent.id}`);
    } else {
      history.push(`/events/edit/${createdEvent.id}`);
    }

    onCollapse();
  };

  const onClose = () => {
    setIsChooseDateModalOpen(false);
    setEventType(initialEventType);

    if (isFromRuleset) {
      history.push(`/events/view/rulesets`);
    } else if (isFromEventDetails) {
      history.push(`/events/events-details`);
    } else {
      history.goBack();
    }

    onCollapse();
  };

  const handleDateSaveClick = async (isV2, createdEvent) => {
    const selectedDateValue = isV2 ? date2 : date;

    let dates;

    if (isV2) {
      const selectedLocalDate = getSelectedLocalDate(selectedDateValue);
      const purchaseStartLocalDate = moment(selectedLocalDate, "MM/DD/YYYY")
        .subtract(14, "days")
        .format("MM/DD/YYYY");

      const isSecondLookMeeting = createdEvent?.event?.name?.value?.includes(
        "Second Look Meeting"
      );

      const eventEndDate = isSecondLookMeeting
        ? buildPacificUtc(selectedDateValue, "22:00")
        : buildPacificUtc(selectedDateValue, "22:30");

      if (createdEvent?.event?.name?.value?.includes("Second Look Meeting")) {
        dates = {
          eventStartDate: buildPacificUtc(selectedDateValue, "20:00"),
          eventEndDate: buildPacificUtc(selectedDateValue, "22:00"),
          eventStartTimeZone: DEFAULT_TIMEZONE_LABEL,
          purchaseStartDate: buildPacificUtcFromDateString(
            purchaseStartLocalDate,
            "10:00"
          ),
          purchaseEndDate: eventEndDate,
          purchaseStartTimeZone: DEFAULT_TIMEZONE_LABEL,
        };
      } else {
        dates = {
          eventStartDate: buildPacificUtc(selectedDateValue, "16:00"),
          eventEndDate: buildPacificUtc(selectedDateValue, "22:30"),
          eventStartTimeZone: DEFAULT_TIMEZONE_LABEL,
          purchaseStartDate: buildPacificUtcFromDateString(
            purchaseStartLocalDate,
            "10:00"
          ),
          purchaseEndDate: eventEndDate,
          purchaseStartTimeZone: DEFAULT_TIMEZONE_LABEL,
        };
      }
    } else {
      const eventStartDateUtc = buildPacificUtc(selectedDateValue, "00:00");

      if (createdEventData?.ruleset?.name.includes("Second Look Meeting")) {
        if (createdEventData?.event?.location?.value == "onsite") {
          dates = {
            eventStartDate: eventStartDateUtc,
            eventStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            purchaseStartDate: moment
              .utc(eventStartDateUtc)
              .subtract(14, "days")
              .toISOString(),
            purchaseStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            purchaseEndDate: moment
              .utc(eventStartDateUtc)
              .add(23.5, "hours")
              .toISOString(),
            slmDoorsOpenDate: moment
              .utc(eventStartDateUtc)
              .add(19, "hours")
              .toISOString(),
            slmSessionStartDate: moment
              .utc(eventStartDateUtc)
              .add(20, "hours")
              .toISOString(),
            slmSessionEndDate: moment
              .utc(eventStartDateUtc)
              .add(22, "hours")
              .toISOString(),
            changeRequestEndDate: moment
              .utc(eventStartDateUtc)
              .add(5, "days")
              .toISOString(),
          };
        } else {
          dates = {
            eventStartDate: eventStartDateUtc,
            eventStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            purchaseStartDate: moment
              .utc(eventStartDateUtc)
              .subtract(14, "days")
              .toISOString(),
            purchaseStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            changeRequestEndDate: moment
              .utc(eventStartDateUtc)
              .add(5, "days")
              .toISOString(),
          };
        }
      } else {
        if (createdEventData?.event?.location?.value == "onsite") {
          dates = {
            eventStartDate: eventStartDateUtc,
            eventStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            purchaseStartDate: moment
              .utc(eventStartDateUtc)
              .subtract(14, "days")
              .toISOString(),
            purchaseStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            seminarDoorsOpenDate: moment
              .utc(eventStartDateUtc)
              .add(14, "hours")
              .toISOString(),
            slmSessionStartDate: moment
              .utc(eventStartDateUtc)
              .add(20, "hours")
              .toISOString(),
            slmSessionEndDate: moment
              .utc(eventStartDateUtc)
              .add(22.5, "hours")
              .toISOString(),
            seminarSessionStartDate: moment
              .utc(eventStartDateUtc)
              .add(15, "hours")
              .toISOString(),
            seminarSessionEndDate: moment
              .utc(eventStartDateUtc)
              .add(1, "days")
              .toISOString(),
            changeRequestEndDate: moment
              .utc(eventStartDateUtc)
              .add(5, "days")
              .toISOString(),
            slmDoorsOpenDate: moment
              .utc(eventStartDateUtc)
              .add(19, "hours")
              .toISOString(),
          };
        } else {
          dates = {
            eventStartDate: eventStartDateUtc,
            eventStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            purchaseStartDate: moment
              .utc(eventStartDateUtc)
              .subtract(14, "days")
              .toISOString(),
            purchaseStartTimeZone: DEFAULT_TIMEZONE_LABEL,
            purchaseEndDate: moment
              .utc(eventStartDateUtc)
              .add(23.5, "hours")
              .toISOString(),
            seminarDoorsOpenDate: moment
              .utc(eventStartDateUtc)
              .add(14, "hours")
              .toISOString(),
            seminarSessionStartDate: moment
              .utc(eventStartDateUtc)
              .add(15, "hours")
              .toISOString(),
            seminarSessionEndDate: moment
              .utc(eventStartDateUtc)
              .add(1, "days")
              .toISOString(),
            slmDoorsOpenDate: moment
              .utc(eventStartDateUtc)
              .add(19, "hours")
              .toISOString(),
            slmSessionStartDate: moment
              .utc(eventStartDateUtc)
              .add(20, "hours")
              .toISOString(),
            slmSessionEndDate: moment
              .utc(eventStartDateUtc)
              .add(22.5, "hours")
              .toISOString(),
            changeRequestEndDate: moment
              .utc(eventStartDateUtc)
              .add(5, "days")
              .toISOString(),
          };
        }
      }
    }

    const selectedLocalDate = getSelectedLocalDate(selectedDateValue);
    const homesitePurchaseStartLocalDate = moment(
      selectedLocalDate,
      "MM/DD/YYYY"
    )
      .subtract(30, "days")
      .format("MM/DD/YYYY");

    const eventObject = {
      event: {
        dates,
      },
      tickets: createdEvent.tickets.map((ticketSet) => {
        return {
          ...ticketSet,
          tickets: ticketSet.tickets.map((ticket) => {
            // Flatten each field to propertyName: saved || value
            const flattenedTicket = Object.entries(ticket).reduce(
              (acc, [key, val]) => {
                if (
                  val &&
                  typeof val === "object" &&
                  ("saved" in val || "value" in val)
                ) {
                  acc[key] = val.saved ?? val.value ?? null;
                } else {
                  acc[key] = val;
                }
                return acc;
              },
              {}
            );

            // Homesite adjustment
            if (flattenedTicket?.name?.toLowerCase?.().includes("homesite")) {
              flattenedTicket.purchaseStartDate = buildPacificUtcFromDateString(
                homesitePurchaseStartLocalDate,
                "10:00"
              );
              flattenedTicket.purchaseStartTimeZone = DEFAULT_TIMEZONE_LABEL;
            }

            return flattenedTicket;
          }),
        };
      }),
    };

    const eventId = isV2 ? createdEvent.id : createdEventData?.id;

    await eventUpdatePut.mutateAsync({ eventObject, eventId });

    if (!isV2) {
      history.push(
        `/events/edit/event/localv${
          createdEventData?.event?.ruleset?.value.includes("2") ? "2" : "1"
        }/${isV2 ? createdEvent.id : createdEventData?.id}`
      );
    }

    onCollapse();
  };

  return (
    <div className={styles.container}>
      <Modal
        title="CREATE NEW EVENT"
        isOpen={isOpen && !isChooseDateModalOpen}
        onClose={onClose}
        closeBtn="no"
        className={styles.modal}
        children={
          <div>
            <div className={styles.grid2}>
              <div className={styles.gridItem}>Ruleset:</div>
              <div className={styles.ruleset}>
                <Select
                  placeholder={eventType.ruleset || "Select"}
                  options={rulesetOptions}
                  onChange={(selectedOption) =>
                    handleRulesetChange(selectedOption)
                  }
                  disabled={isRulesetDataLoading}
                />
              </div>
              <div className={styles.gridItem}>Event Name:</div>
              <div
                className={classNames(styles.eventName, {
                  [styles.disable]: !eventType.ruleset,
                })}
              >
                <Select
                  placeholder={eventType.eventName || "Select"}
                  options={eventNameOptions || []}
                  onChange={(newEventName) =>
                    handleEventNameChange(eventType.ruleset, newEventName)
                  }
                  disabled={!eventType.ruleset || isRulesetDataLoading}
                />
              </div>
            </div>

            <div className={styles.hr} />

            <div className={styles.grid}>
              <div className={styles.location}>Location:</div>
              <div>
                <button
                  className={classNames(
                    styles.onSiteOrVirtualIndicator,
                    eventType.location !== "Virtual"
                      ? styles.onSiteOrVirtualIndicatorOff
                      : ""
                  )}
                  onClick={handleLocation}
                >
                  VIRTUAL
                </button>
                <button
                  className={classNames(
                    styles.onSiteOrVirtualIndicator,
                    eventType.location === "Onsite"
                      ? ""
                      : styles.onSiteOrVirtualIndicatorOff
                  )}
                  onClick={handleLocation}
                >
                  ONSITE
                </button>
              </div>
            </div>

            <div className={styles.hr} />

            {eventType.location === "Onsite" &&
              eventType.eventName !== "" &&
              eventType.eventName !== "Select" && (
                <div className={styles.grid3}>
                  <div className={styles.location}>Link To Virtual Event:</div>
                  {isVirtualEventsLoading || virtualEvents === null ? (
                    <span className={styles.loader}>
                      <Loader />
                    </span>
                  ) : (
                    <>
                      {virtualEvents.length <= 0 ? (
                        <div className={styles.noEvents}>No events found</div>
                      ) : (
                        <div className={styles.virtualEventList}>
                          {virtualEvents
                            .filter(
                              (e) =>
                                e.marketing.title.saved ||
                                e.marketing.title.value
                            )
                            .map((event, index) => (
                              <div
                                className={
                                  index == virtualEvents.length - 1
                                    ? styles.virtualEventLast
                                    : styles.virtualEvent
                                }
                                key={index}
                              >
                                <div className={styles.checkboxContainer}>
                                  <Checkbox
                                    className={styles.checkbox}
                                    onChange={() => {
                                      if (selectedVirtualEventId === event.id) {
                                        setSelectedVirtualEventId(null);
                                      } else {
                                        setSelectedVirtualEventId(event.id);
                                      }
                                    }}
                                    checked={
                                      event.id === selectedVirtualEventId
                                    }
                                  />
                                </div>
                                <div className={styles.name}>
                                  {event.marketing.title.saved ||
                                    event.marketing.title.value}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                  <div className={styles.virtualNote}>
                    Note: Virtual event must be published to be shown in list
                  </div>
                  <div className={styles.divider} />
                </div>
              )}

            {eventType.ruleset == "Local v2.0" && (
              <div className={styles.grid3}>
                <div className={styles.datePickerContainer}>
                  <div>Select Date:</div>
                  <DatePicker
                    dateString={getPickerDateString(date2)}
                    setDate={setDate2}
                    isAdding={true}
                  />
                </div>
              </div>
            )}

            <div>
              <button
                className={classNames(styles.button, {
                  [styles.disable]: !eventType.ruleset || !eventType.eventName,
                })}
                onClick={() => handleCreate()}
                disabled={!eventType.ruleset || !eventType.eventName}
              >
                CREATE
              </button>
            </div>
          </div>
        }
      />

      <Modal
        title="CHOOSE DATE"
        isOpen={isChooseDateModalOpen && !isOpen}
        closeBtn="none"
        className={styles.modal}
        children={
          <div className={styles.chooseDateModalContainer}>
            <div className={styles.labelAndPickerContainer}>
              <DatePicker
                dateString={getPickerDateString(date)}
                setDate={setDate}
                isAdding={true}
              />
            </div>
            <div>
              <button
                className={styles.saveBtn}
                onClick={handleDateSaveClick}
                disabled={!eventType.ruleset || !eventType.eventName}
              >
                SAVE
              </button>
            </div>
          </div>
        }
      />

      <NewEventNameModal
        id="newEventNameModal"
        ruleset={eventType.ruleset}
        isOpen={openNewEventModal}
        onCollapse={() => {
          setOpenNewEventModal(false);
        }}
        onSave={onSaveNewEvent}
      />
    </div>
  );
}

NewEventModal.propTypes = {
  isOpen: PropTypes.bool,
  onCollapse: PropTypes.func,
  eventNames: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  setEventNames: PropTypes.func,
  onChange: PropTypes.func,
  onEdit: PropTypes.bool,
};
